import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {DataObjectId, type Hub, isDeletedEntity} from 'yougile-model'
import {crmStore} from '@components/crm/crm-store'
import {useForceUpdate, useMemoOne, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task as TaskUtil} from '@extensions/task'
import {Task as TaskExtension} from '@extensions/task'
import {chatStore} from './chat-store'
import {ParentTask} from './parent-task'
import {ParentTasksStore, StoreContext, useStore} from './parent-tasks-store'

const TwTaskSelected = tw.div`
    absolute
    bg-accent-old
    w-4
    top-0
    bottom-0
    left-0
    h-full
`

const ParentTaskContainer = observer((props: {taskId: DataObjectId}) => {
  const [state, store] = useStore()
  const forceUpdate = useForceUpdate()

  const task = TaskExtension.getById(props.taskId)
  useSyncEvent('current', {filter: props.taskId}, forceUpdate)

  if (!task || task.deleted) {
    return null
  }
  const subtasks = SubtaskUtil.getSubtaskIds(task)
    .map(x => TaskUtil.getById(x))
    .filter((t): t is Hub => !!t && !isDeletedEntity(t))
  const selected = store.onBoard && chatStore.isOpened(props.taskId)

  return (
    <div className="select-none">
      <div className={cn('relative')}>
        <ParentTask
          id={props.taskId}
          title={task.title}
          isParent={subtasks.length > 0}
          isDeal={crmStore.isDeal(task)}
          isCompleted={task.data?.gtd?.state?.stage === 'done'}
        />
        {selected && store.taskId !== props.taskId && (
          <TwTaskSelected className="-left-1" />
        )}
      </div>
      {state.opened.has(task.id) && (
        <div
          className={cn(
            store.onBoard && 'with-border border-0 border-l',
            'ml-24'
          )}
        >
          {subtasks.map((x, i) => (
            <div key={i} className="relative">
              <ParentTaskContainer taskId={x.id} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

export const ParentTasks = observer(
  (props: {taskId: DataObjectId; onBoard?: boolean}) => {
    const store = useMemoOne(
      () => new ParentTasksStore(props.taskId, !!props.onBoard),
      [props.taskId, props.onBoard]
    )
    useEffect(() => store.dispose, [store])

    if (!store.state.parentTaskIds.length) {
      return null
    }

    return (
      <StoreContext.Provider value={store}>
        {store.state.parentTaskIds.map((taskId, i) => (
          <ParentTaskContainer key={i} taskId={taskId} />
        ))}
      </StoreContext.Provider>
    )
  }
)
